import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  TablePagination,
  Grid,
  Typography,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Person,
  Visibility,
  Add,
  Edit,
  Delete,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { apiService, Customer, Order } from '../../services/apiService';
import { useNavigate } from 'react-router-dom';

const Customers: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Order drawer states
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [orderFormData, setOrderFormData] = useState({
    item_name: '',
    unit_price: '',
    quantity: '',
    campaign_name: '',
    voucher_name: '',
    voucher_number: '',
    period: '',
    created_by: '',
    creation_date: '',
    coordinated_by: '',
    coordinated_date: '',
    feedback_by: '',
    feedback_date: '',
    effective_start_date: '',
    effective_end_date: '',
    followed_by: '',
    follow_status: '',
    collection_date: '',
    collection_time: '',
    notes: '',
    status: 'pending',
  });
  const [items, setItems] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  // Fetch data from API
  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetchCustomers();
  }, []);

  // Fetch items and users for order drawer
  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const [itemsResponse, usersResponse] = await Promise.all([
          apiService.getItems(),
          apiService.getUsers(),
        ]);
        setItems(itemsResponse.data || []);
        setUsers(usersResponse.data || []);
      } catch (error: any) {
        console.error('Error fetching order data:', error);
      }
    };
    
    fetchOrderData();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCustomers();
      // Handle both response structures
      const customersData = Array.isArray(response.data) ? response.data : [];
      setCustomers(customersData);
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        // Token expired or invalid, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        toast.error(error.message || 'Failed to fetch customers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Order drawer handlers
  const handleOpenOrderDrawer = async (customer: Customer, orderToEdit?: Order) => {
    setSelectedCustomer(customer);
    setEditingOrder(orderToEdit || null);
    
    if (orderToEdit) {
      // Populate form with existing order data
      setOrderFormData({
        item_name: orderToEdit.item_name || '',
        unit_price: orderToEdit.unit_price?.toString() || '',
        quantity: orderToEdit.quantity?.toString() || '',
        campaign_name: orderToEdit.campaign_name || '',
        voucher_name: orderToEdit.voucher_name || '',
        voucher_number: orderToEdit.voucher_number || '',
        period: orderToEdit.period || '',
        created_by: orderToEdit.created_by || '',
        creation_date: orderToEdit.creation_date || '',
        coordinated_by: orderToEdit.coordinated_by || '',
        coordinated_date: orderToEdit.coordinated_date || '',
        feedback_by: orderToEdit.feedback_by || '',
        feedback_date: orderToEdit.feedback_date || '',
        effective_start_date: orderToEdit.effective_start_date || '',
        effective_end_date: orderToEdit.effective_end_date || '',
        followed_by: orderToEdit.followed_by || '',
        follow_status: orderToEdit.follow_status || '',
        collection_date: orderToEdit.collection_date || '',
        collection_time: orderToEdit.collection_time || '',
        notes: orderToEdit.notes || '',
        status: orderToEdit.status || 'pending',
      });
    } else {
      // Reset form for new order
      setOrderFormData({
        item_name: '',
        unit_price: '',
        quantity: '',
        campaign_name: '',
        voucher_name: '',
        voucher_number: '',
        period: '',
        created_by: '',
        creation_date: '',
        coordinated_by: '',
        coordinated_date: '',
        feedback_by: '',
        feedback_date: '',
        effective_start_date: '',
        effective_end_date: '',
        followed_by: '',
        follow_status: '',
        collection_date: '',
        collection_time: '',
        notes: '',
        status: 'pending',
      });
    }
    
    setOrderDrawerOpen(true);
  };

  const handleCloseOrderDrawer = () => {
    setOrderDrawerOpen(false);
    setEditingOrder(null);
    setSelectedCustomer(null);
  };

  const handleOrderFormChange = (field: string, value: any) => {
    setOrderFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOrderSubmit = async () => {
    if (!selectedCustomer) return;
    
    try {
      // Format collection_time to H:i:s format
      let formattedCollectionTime = orderFormData.collection_time;
      if (formattedCollectionTime && formattedCollectionTime.length === 5) {
        // If time is in HH:MM format, add :00 for seconds
        formattedCollectionTime = formattedCollectionTime + ':00';
      }

      const orderData = {
        ...orderFormData,
        customer_id: selectedCustomer.id,
        unit_price: parseFloat(orderFormData.unit_price) || 0,
        quantity: parseFloat(orderFormData.quantity) || 0,
        status: orderFormData.status as 'pending' | 'in_progress' | 'completed' | 'cancelled',
        collection_time: formattedCollectionTime || '',
      };

      if (editingOrder) {
        await apiService.updateOrder(editingOrder.id, orderData);
        toast.success('Order updated successfully');
      } else {
        await apiService.createOrder(orderData);
        toast.success('Order created successfully');
      }
      
      handleCloseOrderDrawer();
      fetchCustomers(); // Refresh the list
    } catch (error: any) {
      toast.error(error.message || 'Failed to save order');
    }
  };

  const handleDeleteCustomer = async (customerId: number) => {
    const customer = customers.find(c => c.id === customerId);
    const customerName = customer ? customer.name : 'this customer';
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${customerName}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await apiService.deleteCustomer(customerId);
        
        Swal.fire({
          title: 'Deleted!',
          text: 'Customer has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
        });
        
        fetchCustomers(); // Refresh the customers list
      } catch (error: any) {
        console.error('Error deleting customer:', error);
        
        Swal.fire({
          title: 'Error!',
          text: error.message || 'Failed to delete customer',
          icon: 'error',
          confirmButtonColor: '#3085d6',
        });
      }
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.includes(searchTerm) ||
    customer.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Customers
        </Typography>
        <Button
          variant="contained"
          startIcon={<Person />}
          onClick={() => navigate('/customers/add')}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Customer
        </Button>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ 
                color: '#64748b', 
                fontSize: '1rem',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}>
                Total Customers
              </Typography>
              <Typography variant="h2" sx={{ 
                color: '#1e40af', 
                fontWeight: 'bold',
                fontSize: '2rem',
                lineHeight: 1.2,
                letterSpacing: '-0.02em'
              }}>
                {customers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ 
                color: '#64748b', 
                fontSize: '1rem',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}>
                Active Customers
              </Typography>
              <Typography variant="h2" sx={{ 
                color: '#1e40af', 
                fontWeight: 'bold',
                fontSize: '2rem',
                lineHeight: 1.2,
                letterSpacing: '-0.02em'
              }}>
                {customers.filter(customer => customer.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ 
                color: '#64748b', 
                fontSize: '1rem',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}>
                New Customers (This Month)
              </Typography>
              <Typography variant="h2" sx={{ 
                color: '#1e40af', 
                fontWeight: 'bold',
                fontSize: '2rem',
                lineHeight: 1.2,
                letterSpacing: '-0.02em'
              }}>
                {customers.filter(customer => {
                  const customerDate = new Date(customer.created_at);
                  const currentDate = new Date();
                  return customerDate.getMonth() === currentDate.getMonth() && 
                         customerDate.getFullYear() === currentDate.getFullYear();
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 3 }}>
          {/* Search and Action Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder="Search here"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#4A90E2' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  width: 400,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#e0f2fe',
                    borderColor: '#e0f2fe',
                    '& fieldset': {
                      borderColor: '#e0f2fe',
                    },
                    '&:hover fieldset': {
                      borderColor: '#bae6fd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4A90E2',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#374151',
                    '&::placeholder': {
                      color: '#9ca3af',
                      opacity: 1,
                    },
                  },
                }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{ 
                  px: 3,
                  py: 1.5,
                  borderRadius: '12px',
                  borderColor: '#e0f2fe',
                  backgroundColor: 'white',
                  color: '#4A90E2',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#bae6fd',
                    backgroundColor: '#f0f9ff',
                  },
                }}
              >
                Filter
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              sx={{ 
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                backgroundColor: '#4A90E2',
                color: 'white',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#3b82f6',
                },
              }}
            >
              Export
            </Button>
          </Box>

          {/* Data Table */}
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', height: 'calc(100vh - 450px)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Customer Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Email Address</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Phone Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Loading customers...
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No customers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((customer) => (
                    <TableRow 
                      key={customer.id} 
                      hover 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f9fafb', cursor: 'pointer' },
                        transition: 'all 0.2s ease',
                      }}
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox onClick={(e) => e.stopPropagation()} />
                      </TableCell>
                      <TableCell sx={{ color: '#374151' }}>{customer.name}</TableCell>
                      <TableCell sx={{ color: '#374151' }}>{customer.email}</TableCell>
                      <TableCell sx={{ color: '#374151' }}>{customer.phone}</TableCell>
                      <TableCell sx={{ color: '#374151' }}>{customer.company_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={customer.status}
                          size="small"
                          sx={{
                            backgroundColor: customer.status === 'active' ? '#e0f2f1' : '#fee2e2',
                            color: customer.status === 'active' ? '#0d9488' : '#dc2626',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/customers/${customer.id}`);
                            }}
                            sx={{
                              backgroundColor: '#4A90E2',
                              color: 'white',
                              borderRadius: '6px',
                              textTransform: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#3b82f6',
                              },
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/customers/${customer.id}/edit`);
                            }}
                            sx={{
                              backgroundColor: '#e0f2f1',
                              color: '#0d9488',
                              borderRadius: '6px',
                              textTransform: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#ccedea',
                              },
                            }}
                          >
                            Update
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Add />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenOrderDrawer(customer);
                            }}
                            sx={{
                              backgroundColor: '#f0f9ff',
                              color: '#1e40af',
                              borderRadius: '6px',
                              textTransform: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#dbeafe',
                              },
                            }}
                          >
                            Create Order
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomer(customer.id);
                            }}
                            sx={{
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              borderRadius: '6px',
                              textTransform: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#fecaca',
                              },
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredCustomers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Showing"
            labelDisplayedRows={({ from, to, count }) =>
              `Showing ${from} to ${to} out of ${count} records`
            }
            sx={{
              '& .MuiTablePagination-selectLabel': {
                color: '#6b7280',
              },
              '& .MuiTablePagination-displayedRows': {
                color: '#6b7280',
              },
              '& .MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  color: '#6b7280',
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                  },
                },
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Order Drawer */}
      <Drawer
        anchor="right"
        open={orderDrawerOpen}
        onClose={handleCloseOrderDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 1000 },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e2937' }}>
            {editingOrder ? 'Edit Order' : 'Create New Order'}
            {selectedCustomer && (
              <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>
                for {selectedCustomer.name}
              </Typography>
            )}
          </Typography>

          {/* Customer Selection */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Customer Information
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={customers}
                getOptionLabel={(customer) => `${customer.name} - ${customer.email}`}
                value={selectedCustomer}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    required
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Item Details */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Item Details
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={items}
                getOptionLabel={(item) => `${item.name} - ${item.item_code}`}
                value={items.find(i => i.name === orderFormData.item_name) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleOrderFormChange('item_name', newValue.name);
                    handleOrderFormChange('unit_price', newValue.unit_price.toString());
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Item"
                    required
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={orderFormData.unit_price}
                onChange={(e) => handleOrderFormChange('unit_price', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={orderFormData.quantity}
                onChange={(e) => handleOrderFormChange('quantity', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={orderFormData.campaign_name}
                onChange={(e) => handleOrderFormChange('campaign_name', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Voucher Name"
                value={orderFormData.voucher_name}
                onChange={(e) => handleOrderFormChange('voucher_name', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Voucher Number"
                value={orderFormData.voucher_number}
                onChange={(e) => handleOrderFormChange('voucher_number', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Period"
                value={orderFormData.period}
                onChange={(e) => handleOrderFormChange('period', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={orderFormData.status}
                  onChange={(e) => handleOrderFormChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Order Management */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Order Management
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.name}
                value={users.find(u => u.name === orderFormData.created_by) || null}
                onChange={(event, newValue) => {
                  handleOrderFormChange('created_by', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Created By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Creation Date"
                type="date"
                value={orderFormData.creation_date}
                onChange={(e) => handleOrderFormChange('creation_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.name}
                value={users.find(u => u.name === orderFormData.coordinated_by) || null}
                onChange={(event, newValue) => {
                  handleOrderFormChange('coordinated_by', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Coordinated By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Coordinated Date"
                type="date"
                value={orderFormData.coordinated_date}
                onChange={(e) => handleOrderFormChange('coordinated_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.name}
                value={users.find(u => u.name === orderFormData.feedback_by) || null}
                onChange={(event, newValue) => {
                  handleOrderFormChange('feedback_by', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Feedback By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Feedback Date"
                type="date"
                value={orderFormData.feedback_date}
                onChange={(e) => handleOrderFormChange('feedback_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Effective Start Date"
                type="date"
                value={orderFormData.effective_start_date}
                onChange={(e) => handleOrderFormChange('effective_start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Effective End Date"
                type="date"
                value={orderFormData.effective_end_date}
                onChange={(e) => handleOrderFormChange('effective_end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.name}
                value={users.find(u => u.name === orderFormData.followed_by) || null}
                onChange={(event, newValue) => {
                  handleOrderFormChange('followed_by', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Followed By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Follow Status"
                value={orderFormData.follow_status}
                onChange={(e) => handleOrderFormChange('follow_status', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Collection Date"
                type="date"
                value={orderFormData.collection_date}
                onChange={(e) => handleOrderFormChange('collection_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Collection Time"
                type="time"
                value={orderFormData.collection_time}
                onChange={(e) => handleOrderFormChange('collection_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={orderFormData.notes}
                onChange={(e) => handleOrderFormChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleCloseOrderDrawer}
              sx={{ px: 4 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleOrderSubmit}
              sx={{ px: 4 }}
            >
              {editingOrder ? 'Update Order' : 'Create Order'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Customers;
